import React, {
  useState,
  RefObject,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import { Calendar } from 'react-multi-date-picker';

import {
  CalendarView,
  validateDateRange,
  getLocalDateFromUnix,
  getTimeZoneIdentifier,
} from '../helper';
import { Ref as DateRangePickerRef } from './DateRangePicker';
import { Ref as DataGranularitiesRef } from './DataGranularities';
import { Ref as PreDefinedCalendarFiltersRef } from '../components/PreDefinedCalendarFilters';

import '../styles/dateRangeCalendar.orange.css';

export interface Ref {
  selectedDates: Date[];
  handleCalendarView: (calendarView: CalendarView) => void;
  handleChangeFromAndToDates: (dateFrom: Date, dateTo: Date) => void;
}

interface Props {
  dateTo?: Date;
  dateFrom?: Date;
  dateRangePickerRef: RefObject<DateRangePickerRef>;
  dataGranularitiesRef: RefObject<DataGranularitiesRef>;
  preDefinedCalendarFiltersRef: React.RefObject<PreDefinedCalendarFiltersRef>;
}

const DateRangeCalendar: ForwardRefRenderFunction<Ref, Props> = (
  {
    dateRangePickerRef,
    dateTo = new Date(),
    dataGranularitiesRef,
    dateFrom = new Date(),
    preDefinedCalendarFiltersRef,
  },
  ref
) => {
  const timezone = getTimeZoneIdentifier();

  const [selectedDates, setSelectedDates] = useState<Date[]>([
    dateFrom,
    dateTo,
  ]);

  const [calendarView, setCalendarView] = useState<CalendarView>(
    CalendarView.DAY
  );

  const handleChangeFromAndToDates = (dateFrom: Date, dateTo: Date) => {
    const maxDays = dataGranularitiesRef.current?.selectedGranularity?.maxDays;
    const dateRange = validateDateRange(dateFrom, dateTo, maxDays);

    setSelectedDates([dateRange.dateFrom, dateRange.dateTo]);
  };

  const handleCalendarView = (calendarView: CalendarView) =>
    setCalendarView(calendarView);

  const handleChange = (selectedDates: any) => {
    const parsedDates = selectedDates.map(
      (el) => new Date(getLocalDateFromUnix(el.unix, timezone))
    );
    handleChangeFromAndToDates(parsedDates[0], parsedDates[1]);
    dateRangePickerRef.current?.handleChangeFromAndToDates(
      parsedDates[0],
      parsedDates[1]
    );
    preDefinedCalendarFiltersRef.current?.handleSelectedOptions(undefined);
    preDefinedCalendarFiltersRef.current?.handleShowCustomOption(false);
    preDefinedCalendarFiltersRef.current?.preDefinedCalendarCustomFilterRef.current?.handleChangeEnteredValues(
      { daysFrom: 0, daysTo: 0 }
    );
  };

  useImperativeHandle(ref, () => ({
    selectedDates,
    handleCalendarView,
    handleChangeFromAndToDates,
  }));

  return (
    <Calendar
      className="orange"
      range
      rangeHover
      shadow={false}
      numberOfMonths={
        calendarView === CalendarView.MONTH ||
        calendarView === CalendarView.YEAR
          ? 1
          : 2
      }
      value={selectedDates}
      onChange={handleChange}
      onlyMonthPicker={calendarView === CalendarView.MONTH}
      onlyYearPicker={calendarView === CalendarView.YEAR}
    />
  );
};

export default forwardRef<Ref, Props>(DateRangeCalendar);
